import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  BellAlertIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import {
  Button,
  Card,
  Input,
  LoadingSpinner,
  PageHeader
} from '../components/ui';
import notificationService from '../services/notification.service';

type ExtraEmail = { email: string; name: string; isActive: boolean };

type PreferenceOption = { id: string; label: string };

type PreferencesData = {
  receiveInApp: boolean;
  receivePrimaryEmail: boolean;
  enabledEventCategories: string[];
  enabledScheduledTypes: string[];
  extraEmails: ExtraEmail[];
  primaryEmail: string;
  maxExtraEmailsPerUser: number;
  limits: {
    maxExtraEmails: number;
    eventCategories: PreferenceOption[];
    scheduledTypes: PreferenceOption[];
  };
};

const NotificationPreferences: React.FC = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PreferencesData | null>(null);

  const { isLoading } = useQuery('notificationPreferences', () => notificationService.getPreferences(), {
    onSuccess: (res) => {
      const d = res?.data;
      if (!d) return;
      setForm({
        receiveInApp: d.receiveInApp,
        receivePrimaryEmail: d.receivePrimaryEmail,
        enabledEventCategories: d.enabledEventCategories || [],
        enabledScheduledTypes: d.enabledScheduledTypes || [],
        extraEmails: (d.extraEmails || []).map((row: ExtraEmail) => ({
          email: row.email,
          name: row.name || '',
          isActive: row.isActive !== false
        })),
        primaryEmail: d.primaryEmail,
        maxExtraEmailsPerUser: d.maxExtraEmailsPerUser ?? d.limits?.maxExtraEmails ?? 3,
        limits: d.limits
      });
    }
  });

  const saveMutation = useMutation(
    (payload: Record<string, unknown>) => notificationService.updatePreferences(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notificationPreferences');
        toast.success('Preferencias guardadas');
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.message || err.message || 'Error al guardar');
      }
    }
  );

  const toggleCategory = (id: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      const has = prev.enabledEventCategories.includes(id);
      const next = has
        ? prev.enabledEventCategories.filter((c) => c !== id)
        : [...prev.enabledEventCategories, id];
      return { ...prev, enabledEventCategories: next };
    });
  };

  const toggleScheduled = (id: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      const has = prev.enabledScheduledTypes.includes(id);
      const next = has
        ? prev.enabledScheduledTypes.filter((c) => c !== id)
        : [...prev.enabledScheduledTypes, id];
      return { ...prev, enabledScheduledTypes: next };
    });
  };

  const addExtraEmail = () => {
    setForm((prev) => {
      if (!prev) return prev;
      if (prev.extraEmails.length >= prev.maxExtraEmailsPerUser) return prev;
      return {
        ...prev,
        extraEmails: [...prev.extraEmails, { email: '', name: '', isActive: true }]
      };
    });
  };

  const updateExtra = (index: number, patch: Partial<ExtraEmail>) => {
    setForm((prev) => {
      if (!prev) return prev;
      const copy = [...prev.extraEmails];
      copy[index] = { ...copy[index], ...patch };
      return { ...prev, extraEmails: copy };
    });
  };

  const removeExtra = (index: number) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        extraEmails: prev.extraEmails.filter((_, i) => i !== index)
      };
    });
  };

  const handleSave = () => {
    if (!form) return;
    saveMutation.mutate({
      receiveInApp: form.receiveInApp,
      receivePrimaryEmail: form.receivePrimaryEmail,
      enabledEventCategories: form.enabledEventCategories,
      enabledScheduledTypes: form.enabledScheduledTypes,
      extraEmails: form.extraEmails.filter((row) => row.email.trim())
    });
  };

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const eventOptions = form.limits?.eventCategories || [];
  const scheduledOptions = form.limits?.scheduledTypes || [];
  const slotsUsed = form.extraEmails.length;
  const slotsMax = form.maxExtraEmailsPerUser;

  return (
    <div className="space-y-8 animate-fade-in-up max-w-4xl mx-auto">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link
          to="/notifications"
          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Notificaciones
        </Link>
      </div>

      <PageHeader
        title="Mis preferencias de notificación"
        subtitle="Elige qué alertas recibes en la app, en tu correo principal y en correos adicionales."
      />

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <BellAlertIcon className="h-6 w-6 text-primary-600" />
          <h2 className="text-lg font-semibold text-slate-900">Canales generales</h2>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-primary-600"
            checked={form.receiveInApp}
            onChange={(e) => setForm((p) => (p ? { ...p, receiveInApp: e.target.checked } : p))}
          />
          <span className="text-sm text-slate-700">Recibir notificaciones en la aplicación</span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer mt-4">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-primary-600"
            checked={form.receivePrimaryEmail}
            onChange={(e) => setForm((p) => (p ? { ...p, receivePrimaryEmail: e.target.checked } : p))}
          />
          <span className="text-sm text-slate-700">
            Recibir correos en mi cuenta ({form.primaryEmail})
          </span>
        </label>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Eventos en tiempo real</h2>
        <p className="text-sm text-slate-500 mb-4">
          Tipos de actividad del sistema que quieres recibir al instante.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {eventOptions.map((opt) => (
            <label key={opt.id} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
                checked={form.enabledEventCategories.includes(opt.id)}
                onChange={() => toggleCategory(opt.id)}
              />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </label>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Correos programados</h2>
        <p className="text-sm text-slate-500 mb-4">
          Resúmenes y recordatorios automáticos (si tu funeraria los tiene activos).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {scheduledOptions.map((opt) => (
            <label key={opt.id} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
                checked={form.enabledScheduledTypes.includes(opt.id)}
                onChange={() => toggleScheduled(opt.id)}
              />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </label>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <EnvelopeIcon className="h-6 w-6 text-primary-600" />
          <h2 className="text-lg font-semibold text-slate-900">Correos adicionales</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Puedes añadir hasta {slotsMax} correo(s) extra. Usados: {slotsUsed}/{slotsMax}.
        </p>
        <div className="space-y-3">
          {form.extraEmails.length === 0 ? (
            <p className="text-sm text-slate-500">No tienes correos adicionales configurados.</p>
          ) : (
            form.extraEmails.map((row, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                <div className="md:col-span-5">
                  <Input
                    label="Email"
                    value={row.email}
                    onChange={(v) => updateExtra(idx, { email: v })}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                <div className="md:col-span-4">
                  <Input
                    label="Nombre (opcional)"
                    value={row.name}
                    onChange={(v) => updateExtra(idx, { name: v })}
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-2 pb-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={row.isActive}
                      onChange={(e) => updateExtra(idx, { isActive: e.target.checked })}
                    />
                    Activo
                  </label>
                </div>
                <div className="md:col-span-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => removeExtra(idx)}>
                    Quitar
                  </Button>
                </div>
              </div>
            ))
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addExtraEmail}
            disabled={slotsUsed >= slotsMax}
          >
            Añadir correo
          </Button>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button variant="danger" onClick={handleSave} loading={saveMutation.isLoading}>
          Guardar preferencias
        </Button>
      </div>
    </div>
  );
};

export default NotificationPreferences;
